import React, { useState, useEffect, useMemo } from 'react'
import MaterialTable from '@material-table/core'

import { MuiThemeProvider, makeStyles } from '@material-ui/core/styles'

import { table } from '../../services/theme'

import { Button, Typography, MenuItem, Tooltip } from '@material-ui/core'

import { useDispatch, useSelector } from 'react-redux'

import { updateHousehold } from '../../actions/households/households.actions'
import {
	createCredits as createDaycareCredits,
	deleteCredits as deleteDaycareCredits
} from '../../actions/credits/daycare.actions'
import {
	createCredits as createWalkCredits,
	deleteCredits as deleteWalkCredits
} from '../../actions/credits/walk.actions'
import { showSuccess, showError } from '../../actions/misc/misc.actions'
import { DAYCARE, DAYPASS, GROOMING } from '../../utils'
import { AutoRefillDialog } from '../Dialogs'

import TableDropdown from './TableDropdown'

import { merge } from 'lodash'
import AddCreditDialog from '../Dialogs/Payments/AddCreditDialog'
import RemoveCreditDialog from '../Dialogs/Payments/RemoveCreditDialog'
import AddOnBankDialog from '../Dialogs/Payments/AddonBankDialog'
import AddDaypassCreditDialog from '../Dialogs/Payments/AddDaypassCreditDialog'
import useCurrentUser from '../../hooks/useCurrentUser'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
	tableWrapper: {
		'& table': {
			backgroundColor: 'transparent !important',
			background: 'transparent !important'
		},
		'& tr': {
			backgroundColor: 'transparent !important',
			background: 'transparent !important'
		},
		'& th': {
			color: '#1A1A35',
			fontWeight: 800
		},
		'& td': {
			backgroundColor: 'transparent !important',
			background: 'transparent !important',
			border: 'none'
		}
	},
	autoRefill: {
		'& > div': {
			fontSize: 14,
			lineHeight: 19,
			display: 'flex',
			alignItems: 'center'
		},
		'& button': {
			color: '#6B4AD6'
		}
	},
	onText: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: '600'
	},
	availableWithTooltip: {
		cursor: 'help',
		borderBottom: '1px dotted rgba(26, 26, 53, 0.4)'
	},
	facilityBreakdown: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		padding: '2px 0',
		fontFamily: 'Nunito',
		fontSize: 12,
		lineHeight: '16px',
		fontWeight: 600
	}
}))

const TableExample = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const household = useSelector((state) => state.specific.households.selected)
	const facility = useSelector((state) => state.specific.facilities.selected)
	const error = useSelector((state) => state.specific.households.error)
	const { isBackOfficeUser } = useCurrentUser()
	let initialAutoRenew = {
		20: null,
		40: null,
		60: null,
		11: null,
		daycare: null
	}
	const [adding, setAdding] = useState(false)
	const [isAddOnBankShown, setIsAddOnBankShown] = useState(false)
	const [autoRenew, setAutoRenew] = useState(initialAutoRenew)
	const [isAutoRenewDialogShown, setIsAutoRenewDialogShown] = useState(false)
	const [isAddCreditDialogShown, setIsAddCreditDialogShown] = useState(false)
	const [isRemoveCreditDialogShown, setIsRemoveCreditDialogShown] = useState(false)
	const [isDaypassDialogShown, setIsDaypassDialogShown] = useState(false)
	const [addingDaypass, setAddingDaypass] = useState(false)
	const [selectedCredit, setSelectedCredit] = useState(null)


	useEffect(() => {
		if (household) {
			setAutoRenew(merge(autoRenew, household.auto_renew))
		}
	}, [household])

	// Memberships that receive monthly bath / grooming member credits share this refill date
	const BATH_CREDIT_MEMBERSHIP_TYPES = [
		'grooming_annual',
		'grooming_monthly',
		'vip_annual',
		'vip_monthly',
		'founding'
	]

	const membershipForGroomingRefill = useMemo(() => {
		const active = (household?.memberships || []).filter(
			(m) =>
				BATH_CREDIT_MEMBERSHIP_TYPES.includes(m.type) &&
				!m.deactivated &&
				!m.expired &&
				!m.upcoming
		)
		// Prefer an explicit grooming plan when present
		return (
			active.find((m) => m.type.includes('grooming')) ||
			active.find((m) => !!m.addon_refill_date) ||
			null
		)
	}, [household])

	useEffect(() => {
		if (!isAutoRenewDialogShown) {
			setSelectedCredit(null)
		}
	}, [isAutoRenewDialogShown])

	const removeAutoRenew = (credit) => {
		if (credit) {
			let creditType = credit.type || DAYCARE

			let updates = {
				auto_renew: autoRenew
			}

			if (credit.autoRefillAmount) {
				updates.auto_renew[creditType] = null
			}

			dispatch(updateHousehold(household._id, updates)).then(() => {
				setIsAutoRenewDialogShown(false)

				if (!error) {
					dispatch(showSuccess('Auto renew disabled successfully.'))
				}
			})
		}
	}

	const showAutoRefillDialog = (credit) => {
		setSelectedCredit(credit)
		setIsAutoRenewDialogShown(true)
	}

	const formatCreditCount = (credits) =>
		Number.isInteger(credits) ? credits : Number(Number(credits).toFixed(2))

	const renderAvailableCredits = (data) => {
		const available = data.availableCredits
		if (data.type !== DAYCARE || !data.facilityBreakdown?.length) {
			return available
		}

		return (
			<Tooltip
				title={
					<div className={classes.facilityBreakdown}>
						{data.facilityBreakdown.map((row) => (
							<div key={row.facilityId}>
								{row.name}: {formatCreditCount(row.credits)}
							</div>
						))}
					</div>
				}>
				<span className={classes.availableWithTooltip}>{available}</span>
			</Tooltip>
		)
	}

	const renderAutoRefill = (data) => {
		if (data.type === GROOMING) {
			const refillDate = membershipForGroomingRefill?.addon_refill_date
			return (
				<div className={classes.autoRefill}>
					<div>
						<Typography>
							{refillDate ? moment(refillDate).format('MM/DD/YYYY') : '--'}
						</Typography>
					</div>
				</div>
			)
		}
		if (data.autoRefill.enabled === 'n/a')
			return (
				<div className={classes.autoRefill}>
					<div>
						<Typography>--</Typography>
					</div>
				</div>
			)

		return (
			<div className={classes.autoRefill}>
				{!data.autoRefill.enabled ? (
					<div>
						<Button style={{ marginLeft: 5 }} onClick={() => showAutoRefillDialog(data)}>
							Enable
						</Button>
					</div>
				) : (
					<div>
						<Typography className={classes.onText}>On</Typography>
						<Button onClick={() => removeAutoRenew(data)}>Disable</Button>
					</div>
				)}
			</div>
		)
	}

	const updateAutoRenew = (credit) => {
		if (!credit.autoRefillAmount) {
			dispatch(showError('Please select a refill amount.'))
			return
		}

		if (credit) {
			let creditType = credit.type || DAYCARE

			let updates = {
				auto_renew: autoRenew
			}

			if (credit.autoRefillAmount) {
				updates.auto_renew[creditType] = credit.autoRefillAmount
			}

			dispatch(updateHousehold(household._id, updates)).then(() => {
				setIsAutoRenewDialogShown(false)

				if (!error) {
					dispatch(showSuccess('Auto renew updated successfully.'))
				}
			})
		}
	}

	const addCredits = (payload) => {
		if (!isBackOfficeUser) {
			dispatch(showError('Only back office users can add credits.'))
			return
		}
		if (payload && household && facility) {
			if (payload.service === DAYCARE || payload.service === GROOMING) {
				dispatch(createDaycareCredits(payload.facilityId, household._id, payload))
			} else {
				dispatch(createWalkCredits(household._id, payload))
			}
		}
	}

	const removeCredits = (payload) => {
		if (payload && household && facility) {
			if (payload.service === DAYCARE || payload.service === GROOMING) {
				dispatch(deleteDaycareCredits(payload.facilityId, household._id, payload))
			} else {
				dispatch(deleteWalkCredits(household._id, payload))
			}
		}
	}

	return (
		<div>
			<MuiThemeProvider theme={table}>
				<div className={classes.tableWrapper}>
					<MaterialTable
						classes={classes.creditsTable}
						options={{
							showTitle: false,
							search: false,
							paging: false,
							toolbar: false,
							sorting: false,
							grouping: false,
							actionsColumnIndex: -1,
							rowStyle: {
								backgroundColor: 'transparent',
								background: 'transparent',
								border: 'none'
							},
							headerStyle: {
								backgroundColor: 'transparent'
							}
						}}
						localization={{
							header: {
								actions: ''
							}
						}}
						actions={[{}]}
						columns={[
							{ title: 'Service', field: 'service' },
							{
								title: 'Available',
								field: 'availableCredits',
								render: (data) => renderAvailableCredits(data)
							},
							{ title: 'Refill Amount', field: 'autoRefillAmount' },
							{
								title: 'Auto Refill',
								field: 'autoRefill',
								render: (data) => renderAutoRefill(data)
							}
						]}
						data={props.data}
						style={{
							backgroundColor: 'transparent',
							background: 'transparent',
							boxShadow: 'none'
						}}
						components={{
							Action: (props) => {
								if (props.data.type === DAYPASS) {
									return (
										<TableDropdown>
											<Tooltip
												title={
													isBackOfficeUser ? '' : 'Only back office users can add daypasses'
												}>
												<span>
													<MenuItem
														disabled={!isBackOfficeUser}
														onClick={(e) => {
															setIsDaypassDialogShown(true)
															setAddingDaypass(true)
														}}>
														Add Daypass
													</MenuItem>
												</span>
											</Tooltip>
											<MenuItem
												onClick={(e) => {
													setIsDaypassDialogShown(true)
													setAddingDaypass(false)
												}}>
												Remove Daypass
											</MenuItem>
										</TableDropdown>
									)
								}
								return (
									<TableDropdown>
										<Tooltip
											title={
												isBackOfficeUser || props.data.type === 'addon'
													? ''
													: 'Only back office users can add credits'
											}>
											<span>
												<MenuItem
													disabled={!isBackOfficeUser && props.data.type !== 'addon'}
													onClick={(e) => {
														// if (props?.data?.type === GROOMING) {
														// 	return
														// }
														if (props.data.type !== 'addon') {
															setSelectedCredit(props.data)
															setIsAddCreditDialogShown(true)
														} else {
															setIsAddOnBankShown(true)
															setAdding(true)
														}
													}}>
													Add Credits
												</MenuItem>
											</span>
										</Tooltip>
										<MenuItem
											onClick={(e) => {
												// if (props?.data?.type === GROOMING) {
												// 	return
												// }
												if (props.data.type !== 'addon') {
													setSelectedCredit(props.data)
													setIsRemoveCreditDialogShown(true)
												} else {
													setIsAddOnBankShown(true)
													setAdding(false)
												}
											}}>
											Remove Credits
										</MenuItem>
										{props.data.type !== 'addon' && props.data.type !== GROOMING && (
											<MenuItem
												onClick={(e) => {
													setSelectedCredit(props.data)
													setIsAutoRenewDialogShown(true)
												}}>
												Auto Refill
											</MenuItem>
										)}
									</TableDropdown>
								)
							}
						}}
					/>
				</div>
			</MuiThemeProvider>
			<AddCreditDialog
				isShown={isAddCreditDialogShown}
				setIsShown={setIsAddCreditDialogShown}
				selectedCredit={selectedCredit}
				addCredits={addCredits}
				householdId={household?._id}
			/>
			<RemoveCreditDialog
				isShown={isRemoveCreditDialogShown}
				setIsShown={setIsRemoveCreditDialogShown}
				selectedCredit={selectedCredit}
				removeCredits={removeCredits}
			/>
			<AutoRefillDialog
				isShown={isAutoRenewDialogShown}
				setIsShown={setIsAutoRenewDialogShown}
				selectedCredit={selectedCredit}
				updateAutoRenew={updateAutoRenew}
				removeAutoRenew={removeAutoRenew}
			/>
			<AddOnBankDialog isShown={isAddOnBankShown} adding={adding} setIsShown={setIsAddOnBankShown} />
			<AddDaypassCreditDialog
				isShown={isDaypassDialogShown}
				adding={addingDaypass}
				setIsShown={setIsDaypassDialogShown}
			/>
		</div>
	)
}

export default TableExample
